import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Flex,
  Icon,
  Link,
  Snackbar,
  Text,
  TextButton,
} from '@amzn/storm-ui';
import useIntl from '@i18n/useIntl';
import { SNACKBAR_POSITION } from './style';
import { ucmAllCampaignsUrl } from 'src/shared/routes';
import {
  UPGRADE_ACCOUNTS_FAILURE_BANNER_KEY,
  UPGRADE_ACCOUNTS_FAILURE_BANNER_ONE_KEY,
  UPGRADE_ACCOUNTS_GO_TO_CAMPAIGNS_KEY,
  UPGRADE_ACCOUNTS_IN_PROGRESS_SNACKBAR_KEY,
  UPGRADE_ACCOUNTS_IN_PROGRESS_SNACKBAR_ONE_KEY,
  UPGRADE_ACCOUNTS_SUCCESS_BANNER_KEY,
  UPGRADE_ACCOUNTS_SUCCESS_BANNER_ONE_KEY,
  UPGRADE_ACCOUNTS_VIEW_ACCOUNTS_KEY,
} from '@features/combine-accounts/constants';
import {
  FailedUpgrade,
  OutstandingUpgrade,
  UpgradedAccount,
  useUpgradeProgress,
} from '../../upgradeChoose/useUpgradeProgress';

/**
 * What the upgrade page hands over when it navigates here.
 *
 * All of it is optional because arriving with none of it is the normal case: most visits to this
 * page have nothing to do with an upgrade.
 */
export interface UpgradeNavigationState {
  /** Upgrades still running, to be followed to completion from here. */
  outstandingUpgrades?: OutstandingUpgrade[];
  /** Accounts the upgrade endpoint refused at submit, already known before the hand-over. */
  upgradeFailures?: FailedUpgrade[];
  /** Accounts already known to have been upgraded before the hand-over. */
  upgradedAccounts?: UpgradedAccount[];
  /**
   * Display names for the chosen accounts, keyed by DSP account id.
   *
   * Carried across the navigation because the banner names accounts the way the user chose them,
   * and neither the upgrade response nor the status read returns a name. The names are already on
   * screen when the request is submitted, so passing them is cheaper than fetching them again here.
   */
  accountNames?: Record<string, string>;
}

/** What is known about the upgrade once the watch has stopped. */
interface Outcome {
  upgraded: UpgradedAccount[];
  failed: FailedUpgrade[];
  /**
   * Accounts that never reached a terminal state before the follow deadline.
   *
   * Reported as still running rather than as either outcome. The status read has no failure value,
   * so an account that fails after being accepted is indistinguishable from a slow one, and
   * claiming either would assert something nothing observed.
   */
  pending: string[];
}

/** Each message dismisses on its own, so hiding one does not take the others with it. */
type Message = 'progress' | 'pending' | 'failure' | 'success';

/**
 * Reports the progress and outcome of an upgrade on the combine and upgrade landing page.
 *
 * <h3>Why this lives here rather than on the upgrade page</h3>
 *
 * An upgrade that takes longer than a few seconds stops holding the user on the choose page: the
 * design returns them here and reports progress in a snackbar while the work continues. That is a
 * navigation, so the page that started the upgrade is unmounted and cannot be the one that sees it
 * finish. The ids arrive in router state and the watching resumes here.
 *
 * <h3>Why it does not survive a reload</h3>
 *
 * The hand-over rides on router state, which a reload discards, so a reload loses the report while
 * the upgrade itself carries on server-side. That is deliberate: this reports a transient operation,
 * and persisting it would need a rule for when a stale entry expires, which is more machinery than
 * a progress message earns.
 *
 * Renders nothing at all when there is no upgrade to report, which is most of the time.
 */
const UpgradeProgressNotifier: FC = () => {
  const { intl } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { followUpgrades } = useUpgradeProgress();

  const [watchingCount, setWatchingCount] = useState(0);
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [accountNames, setAccountNames] = useState<Record<string, string>>({});
  const [dismissed, setDismissed] = useState<ReadonlySet<Message>>(new Set());
  const [accountsShown, setAccountsShown] = useState(false);

  /*
    The hand-over is a one-time event, so it is captured on the first render and
    the watch below runs once, off empty dependencies.

    Reading it reactively instead was a defect: the effect clears the router state
    once consumed, which changes `location.state`, which would re-run an effect
    that depends on it. React tears down before re-running, so the clearing
    aborted the watch it had just started, and a guard against starting twice then
    stopped it restarting. The snackbar stayed up for ever with no outcome.

    A ref initialiser runs once, so this holds the state as it arrived even though
    the render that follows the clearing sees null.
  */
  const handoverRef = useRef<UpgradeNavigationState | null>(
    (location.state ?? null) as UpgradeNavigationState | null,
  );
  /*
    Held in refs for the same reason: naming them as dependencies would make the
    watch restartable, and a second watch on one hand-over doubles the requests
    and races two reports against each other.
  */
  const pathnameRef = useRef(location.pathname);
  const followRef = useRef(followUpgrades);
  followRef.current = followUpgrades;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  const dismiss = useCallback((message: Message) => {
    setDismissed((previous) => new Set(previous).add(message));
  }, []);

  useEffect(() => {
    const handover = handoverRef.current;
    const outstanding = handover?.outstandingUpgrades ?? [];
    const knownUpgraded = handover?.upgradedAccounts ?? [];
    const knownFailed = handover?.upgradeFailures ?? [];

    if (
      outstanding.length === 0 &&
      knownUpgraded.length === 0 &&
      knownFailed.length === 0
    ) {
      // An ordinary visit to this page, with no upgrade to report.
      return undefined;
    }

    setAccountNames(handover?.accountNames ?? {});
    /*
      Consumed, so the hand-over is cleared from the history entry. Left in place
      it would be read again on a later back-navigation and report an upgrade
      that finished long ago, or restart a watch on accounts already settled.
      Everything needed was captured into refs and state before this point.
    */
    navigateRef.current(pathnameRef.current, { replace: true, state: null });

    /*
      Reported straight away rather than held back until the watch finishes. An
      account refused at submit already has its outcome, and hiding that behind a
      wait of up to the follow deadline tells the user nothing for two minutes
      about something already known.
    */
    setOutcome({
      upgraded: knownUpgraded,
      failed: knownFailed,
      pending: [],
    });

    // Arrived with the upgrade already settled, so there is nothing to follow.
    if (outstanding.length === 0) {
      return undefined;
    }

    /*
      Counts what is actually in flight. Neither an account that has finished nor
      one refused at submit is being upgraded, so folding either into this count
      would overstate it.
    */
    setWatchingCount(outstanding.length);

    const controller = new AbortController();
    let abandoned = false;

    followRef
      .current(outstanding, controller.signal)
      .then((result) => {
        if (abandoned) {
          return;
        }
        setWatchingCount(0);
        /*
          Accounts still pending at the deadline are deliberately reported as
          neither upgraded nor failed. Counting them as either would claim an
          outcome nothing observed.

          `result.failed` is always empty, since only a submit refusal can fail an
          upgrade and submit happened before these ids were handed over, so the
          failures reported here are the ones that arrived with the hand-over.
        */
        setOutcome({
          upgraded: [...knownUpgraded, ...result.upgraded],
          failed: [...knownFailed, ...result.failed],
          pending: result.pending,
        });

        /*
          A dismissal covers what was on screen at the time, not everything that
          happens afterwards. On a partial hand-over the banner appears at once
          with the accounts already done, so a user can dismiss it before the rest
          finish; leaving it dismissed would hide those accounts permanently, and
          with them the only route to their campaigns. New arrivals therefore
          bring it back rather than being suppressed by a stale dismissal.

          Only success grows this way. A refusal is known before the watch starts
          and `pending` is written once, at the end, so neither can outlive its own
          dismissal.
        */
        if (result.upgraded.length > 0) {
          setDismissed((previous) => {
            if (!previous.has('success')) {
              return previous;
            }
            const next = new Set(previous);
            next.delete('success');
            return next;
          });
        }
      })
      .catch(() => {
        /*
          followUpgrades swallows read errors and reports those accounts as
          pending, so reaching here means something unexpected. Stop the spinner
          rather than leaving it running for ever, and keep reporting what arrived
          with the hand-over so a partial outcome is not lost with it.
        */
        if (!abandoned) {
          setWatchingCount(0);
          setOutcome({
            upgraded: knownUpgraded,
            failed: knownFailed,
            pending: outstanding.map((entry) => entry.dspAccountId),
          });
        }
      });

    return () => {
      abandoned = true;
      /*
        Stops the poll loop itself, not just the reporting. Without this, leaving
        the page keeps one request per round in flight until the follow deadline
        expires, for a report nothing is left to render.
      */
      controller.abort();
    };
  }, []);

  /**
   * Resolves a count-bearing message, picking the singular entry for exactly one.
   *
   * This package uses no ICU plurals, so the alternative is copy that reads "1 accounts".
   */
  const countMessage = (oneKey: string, manyKey: string, count: number) =>
    count === 1
      ? intl.getMessage(oneKey)
      : intl.formatMessage(manyKey, { count });

  /**
   * Prefers the name the user chose the account by, falling back to its id.
   *
   * Checks for a blank name rather than only a missing one: `??` falls back on null and undefined
   * but not on an empty string, which would render an empty cell beside the campaigns link.
   */
  const labelFor = (dspAccountId: string) => {
    const name = accountNames[dspAccountId];
    return name && name.trim().length > 0 ? name : dspAccountId;
  };

  const watching = watchingCount > 0 && !dismissed.has('progress');
  const pendingCount = outcome?.pending.length ?? 0;
  const failedCount = outcome?.failed.length ?? 0;
  const upgradedCount = outcome?.upgraded.length ?? 0;

  const showPending = pendingCount > 0 && !dismissed.has('pending');
  const showFailure = failedCount > 0 && !dismissed.has('failure');
  const showSuccess = upgradedCount > 0 && !dismissed.has('success');

  return (
    <>
      {/*
        One positioned container for all three, stacked. They share a fixed
        corner, so rendered in separate containers a partial outcome put the
        failure and still-running messages on top of each other.
      */}
      {(watching || showPending || showFailure) && (
        <div style={SNACKBAR_POSITION}>
          <Flex direction="column" rowGap="small">
            {watching && (
              <Snackbar
                id="upgrade-in-progress"
                variant="loading"
                withCloseButton
                /*
                  Dismissing hides the notification, it does not stop the
                  watching: the polling continues and the outcome is still
                  reported, because the close button dismisses a message rather
                  than cancelling an upgrade already running server-side.
                */
                onClose={() => dismiss('progress')}
                data-testid="upgrade-in-progress-snackbar"
              >
                {countMessage(
                  UPGRADE_ACCOUNTS_IN_PROGRESS_SNACKBAR_ONE_KEY,
                  UPGRADE_ACCOUNTS_IN_PROGRESS_SNACKBAR_KEY,
                  watchingCount,
                )}
              </Snackbar>
            )}

            {/*
              Accounts that never settled before the follow deadline. Reported as
              still being upgraded, without a spinner, because that is what is
              known: the combination continues server-side, and with no failure
              value on the status read neither outcome can be claimed. Rendering
              nothing here left the user with a spinner that stopped and no
              message at all, which is the shape every real failure takes.
            */}
            {showPending && (
              <Snackbar
                id="upgrade-pending"
                variant="info"
                withCloseButton
                onClose={() => dismiss('pending')}
                data-testid="upgrade-pending-snackbar"
              >
                {countMessage(
                  UPGRADE_ACCOUNTS_IN_PROGRESS_SNACKBAR_ONE_KEY,
                  UPGRADE_ACCOUNTS_IN_PROGRESS_SNACKBAR_KEY,
                  pendingCount,
                )}
              </Snackbar>
            )}

            {/*
              Any failure is reported the same way, whether everything failed or
              only some of it did, and only as a count. GRS's per-account reasons
              are not customer-facing copy, and a combination that fails after
              being accepted reports no reason at all.

              On a partial outcome this shows alongside the success banner rather
              than instead of it, so the accounts that did upgrade stay
              reachable.
            */}
            {showFailure && (
              <Snackbar
                id="upgrade-failed"
                variant="error"
                withCloseButton
                onClose={() => dismiss('failure')}
                data-testid="upgrade-failure-snackbar"
              >
                {countMessage(
                  UPGRADE_ACCOUNTS_FAILURE_BANNER_ONE_KEY,
                  UPGRADE_ACCOUNTS_FAILURE_BANNER_KEY,
                  failedCount,
                )}
              </Snackbar>
            )}
          </Flex>
        </div>
      )}

      {/*
        Rendered whenever anything upgraded, including alongside the failure
        snackbar on a partial outcome: the accounts that succeeded still need to
        be reachable, and suppressing this hid them behind the failure.

        Deliberately does not navigate anywhere, even for a single upgraded
        account. The choose page sends a clean single success straight to campaign
        manager, but by the time this component has an outcome the user was
        returned here minutes ago and has moved on; taking the page out from under
        them then is worse than offering the link they can follow when ready.
      */}
      {showSuccess && (
        <Alert
          variant="success"
          withCloseButton
          isOpen
          onClose={() => dismiss('success')}
          data-testid="upgrade-success-banner"
        >
          {/* Body size rather than Alert's header prop: the design sets this
              summary at the same size as descriptive text. */}
          <Text>
            {countMessage(
              UPGRADE_ACCOUNTS_SUCCESS_BANNER_ONE_KEY,
              UPGRADE_ACCOUNTS_SUCCESS_BANNER_KEY,
              upgradedCount,
            )}
          </Text>
          {/*
            The chevron is inside the button rather than beside it, so clicking it
            toggles the list as a user would expect, and so the two sit at the
            gap an icon and its label want. As siblings in a Flex the chevron was
            inert, and the smallest gap the container offered was still a word's
            width apart.
          */}
          <TextButton
            onClick={() => setAccountsShown((shown) => !shown)}
            aria-expanded={accountsShown}
          >
            <Flex
              as="span"
              direction="row"
              alignItems="center"
              columnGap="xxsmall"
            >
              <Icon
                type={accountsShown ? 'angle-up' : 'angle-down'}
                aria-hidden
              />
              {intl.getMessage(UPGRADE_ACCOUNTS_VIEW_ACCOUNTS_KEY)}
            </Flex>
          </TextButton>
          {accountsShown &&
            outcome?.upgraded.map((account) => {
              /*
                Offered only when the link can name the account. Both the
                identifier and the account's host arrive with the completed
                status, and without them campaign manager would open whichever
                account it defaults to, so the action is hidden rather than
                pointed somewhere unintended.
              */
              const campaignsUrl = ucmAllCampaignsUrl(
                account.entityId,
                account.accountUrl,
              );
              return (
                <Flex
                  key={account.dspAccountId}
                  direction="row"
                  columnGap="small"
                  alignItems="center"
                >
                  <Text>{labelFor(account.dspAccountId)}</Text>
                  {campaignsUrl && (
                    <Link href={campaignsUrl}>
                      {intl.getMessage(UPGRADE_ACCOUNTS_GO_TO_CAMPAIGNS_KEY)}
                    </Link>
                  )}
                </Flex>
              );
            })}
        </Alert>
      )}
    </>
  );
};

export default UpgradeProgressNotifier;
